import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

import { PlaygamaCTRContext } from '@/modules/analytics/types'

import { useLoggerStore } from './logger-store'

export const useCtrLoggerStore = defineStore('ctr-observer', () => {
    const observable = ref<IntersectionObserver>()
    const route = useRoute()
    const loggerStore = useLoggerStore()

    // 1. Create a WeakMap to store context for each element
    const contextMap = new WeakMap<HTMLElement, PlaygamaCTRContext>()

    const onClick = (event: MouseEvent) => {
        const element = event.currentTarget as HTMLElement

        const context = contextMap.get(element)
        if (context) {
            loggerStore.logCTREvents({ ...context, eventName: 'click', pageUrl: route.fullPath })
        }
    }

    const onIntersect: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const element = entry.target as HTMLElement

                // Ignore elements that are still loading/collapsed
                if (entry.boundingClientRect.width === 0 || entry.boundingClientRect.height === 0) {
                    return
                }
                observable.value?.unobserve(element)
                const context = contextMap.get(element)

                if (context) {
                    loggerStore.logCTREvents({ ...context, eventName: 'impression', pageUrl: route.fullPath })
                }
            }
        })
    }

    function add(el: HTMLElement, ctrContext: PlaygamaCTRContext) {
        if (!observable.value) {
            observable.value = new IntersectionObserver(onIntersect, { threshold: 1 })
        }

        // remove el to update context
        if (contextMap.has(el)) {
            remove(el)
        }
        contextMap.set(el, ctrContext)
        observable.value.observe(el)
        el.addEventListener('click', onClick)
    }

    function remove(el: HTMLElement) {
        observable.value?.unobserve(el)
        el.removeEventListener('click', onClick)
        contextMap.delete(el)
    }

    function disconnect() {
        observable.value?.disconnect()
        observable.value = undefined
    }

    return {
        add,
        remove,
        disconnect,
    }
})
